/**
 * Normalize a user-entered web link for PREVIEW / header-check use — never for the saved value.
 *
 * Prod semantics (Website app, #1782) allow saving a schemeless URL like `lumas.com` — device
 * players resolve it themselves — but an iframe src resolves anything schemeless RELATIVE to the
 * player origin, bouncing the webplayer preview to its pairing screen, and the header-checker
 * 400s on it. So for those two consumers we approximate the device: prepend `https://`.
 *
 * Returns the preview-safe absolute URL, or null when the value can't be made preview-safe:
 *  - whitespace inside the value (never a URL; `https://lumas com` helps nobody)
 *  - explicit non-http(s) scheme (ftp:// etc. — iframes won't render it, the checker rejects it)
 *  - a normalized (schemeless) value whose hostname has no dot (`https://abc` is a guess we have
 *    no confidence in — keep the last good preview instead of an iframe error). Explicit-scheme
 *    values skip this dot check: `https://localhost:3000` is deliberate and previewable.
 */
export function normalizePreviewUrl(raw: string): string | null {
  const value = raw.trim();
  if (!value || /\s/.test(value)) return null;

  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    // Has an explicit scheme — pass through http(s) as-is, reject everything else.
    if (!/^https?:\/\//i.test(value)) return null;
    try {
      new URL(value);
      return value;
    } catch {
      return null;
    }
  }

  const candidate = `https://${value}`;
  try {
    const url = new URL(candidate);
    if (!url.hostname.includes('.')) return null;
    return candidate;
  } catch {
    return null;
  }
}
